/**
 * Locale-aware formatting helpers shared by the Board screens
 * (deal cards, stage columns, list view, detail drawer).
 */
import { format, formatDistanceToNowStrict } from 'date-fns';
import { ru, enUS } from 'date-fns/locale';
import type { Locale } from '../i18n';

function dfnsLocale(locale: Locale) {
  return locale === 'ru' ? ru : enUS;
}

/** Money in ₽, grouped per locale, no decimals — rendered in --font-mono. */
export function formatMoney(amount: number, locale: Locale): string {
  const grouped = new Intl.NumberFormat(locale === 'ru' ? 'ru-RU' : 'en-US', {
    maximumFractionDigits: 0,
  }).format(amount);
  return `${grouped} ₽`;
}

/** "2 дн" / "3 days" style age from an ISO timestamp. */
export function formatAge(iso: string, locale: Locale): string {
  return formatDistanceToNowStrict(new Date(iso), {
    addSuffix: false,
    locale: dfnsLocale(locale),
  });
}

/** "19 июл 2026" / "19 Jul 2026". */
export function formatDate(iso: string, locale: Locale): string {
  return format(new Date(iso), 'd MMM yyyy', { locale: dfnsLocale(locale) });
}

/** "19 июл 2026, 14:32" — for the transitions timeline. */
export function formatDateTime(iso: string, locale: Locale): string {
  return format(new Date(iso), 'd MMM yyyy, HH:mm', { locale: dfnsLocale(locale) });
}

export function ageInDays(iso: string): number {
  return Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
}
